import React, { useState, useEffect, useCallback } from "react";
import { Card, Tabs, Form, Button, Space, Tooltip, Typography, Spin, Alert, message, Modal } from "antd";
import { PricingAPI } from "../../../../controller/PricingAPI";
import SettingField from "./SettingField";
import { FONT } from "../../../styles/typography";

/**
 * 系統設定頁。
 *
 * 這一頁完全由後端的註冊表驅動，不含任何領域知識 —— 大類、分組、欄位、型別、
 * 動作按鈕全部來自 GET /api/settings。新增一個設定不需要修改這個檔案。
 *
 * 大類用分頁，分組在大類底下堆疊 —— 大類會持續增加，但同一個大類底下的
 * 分組不多，逐個點開反而不利於一次看完整個大類的設定。
 */
export default function SystemSettingsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(null);
  // 動態選項一律延後到使用者展開欄位才查：據點清單要打 Google 兩支 API，
  // 每次開設定頁都查既慢又浪費配額，而多數人開這一頁不是為了改據點。
  const [dynamicOptions, setDynamicOptions] = useState({});

  // 從 Google 授權導回時，結果只在網址列上 —— 顯示完就把參數清掉，
  // 否則重新整理會再跳一次已經過期的訊息。清的時候必須帶上 hash：
  // 這個 app 走 HashRouter，路由在 # 之後，只留 pathname 會讓路由一起消失，
  // 使用者剛授權完就被踢回首頁。
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("google");

    if (!result) return;

    if (result === "ok") {
      message.success("Google 授權完成，已更新 Refresh Token。");
    } else {
      message.error("Google 授權失敗，請重試；詳細原因已寫入系統記錄。");
    }

    window.history.replaceState({}, "", window.location.pathname + window.location.hash);
  }, []);

  const load = useCallback(() => {
    setError(false);
    PricingAPI.settings
      .index()
      .then((res) => {
        setData(res.data);
        setEdited({});
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 只送出被改過的值。整批送出會把機密欄位的空字串一起送去，
  // 後端雖然會當成「不變更」，但送出未經修改的值本身就沒有意義
  const save = () => {
    if (!Object.keys(edited).length) {
      message.info("沒有變更");
      return;
    }
    setSaving(true);
    PricingAPI.settings
      .update(edited)
      .then(() => {
        message.success("已儲存");
        load();
      })
      .catch((e) => {
        const errors = e && e.response && e.response.data && e.response.data.errors;
        message.error(errors ? Object.values(errors)[0][0] : "儲存失敗");
      })
      .finally(() => setSaving(false));
  };

  const clear = (key) => {
    Modal.confirm({
      title: "確定清除這個值？",
      content: "清除後會回復為系統預設值。",
      okText: "清除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: () =>
        PricingAPI.settings
          .clear(key)
          .then(() => {
            message.success("已清除");
            load();
          })
          .catch(() => message.error("清除失敗")),
    });
  };

  // 動作按鈕打的是伺服器已儲存的設定，送不出表單裡未儲存的編輯 —— 例如填完
  // OAuth 憑證直接按「向 Google 取得」，伺服器讀到的還是舊值，必定失敗，
  // 而錯誤訊息會要人「先填好並儲存」，看起來像系統在騙人。停用比讓它失敗好：
  // 儲存鈕在卡片右上角，捲到分組時早已離開畫面，沒有提示根本不會想到。
  const unsavedIn = (groupKey) =>
    Object.keys(edited).some((key) => (data.items.find((i) => i.key === key) || {}).group === groupKey);

  const disabledReason = (groupKey) =>
    unsavedIn(groupKey) ? "這一組有尚未儲存的變更，請先按右上角的「儲存」" : null;

  const loadOptions = (item) => {
    // 有紀錄就不再查 —— 這一格同時擋掉「重複展開」與「請求還在路上又展開一次」
    if (!item.options_from || dynamicOptions[item.key]) return;

    setDynamicOptions((prev) => ({ ...prev, [item.key]: { loading: true, options: [] } }));

    PricingAPI.settings
      .action(item.options_from)
      .then((res) => {
        const { ok, message: msg, options } = res.data;

        if (!ok) message.error(msg || "載入選項失敗");

        setDynamicOptions((prev) => ({
          ...prev,
          [item.key]: { loading: false, options: ok ? options || [] : [] },
        }));
      })
      .catch(() => {
        message.error("載入選項失敗");
        setDynamicOptions((prev) => ({ ...prev, [item.key]: { loading: false, options: [] } }));
      });
  };

  const runAction = (action) => {
    setRunning(action.endpoint);
    PricingAPI.settings
      .action(action.endpoint)
      .then((res) => {
        const { ok, message: msg, detail, redirect_url: redirectUrl } = res.data;
        // 動作可以回一個要導去的網址 (目前只有 Google 授權會用)。整頁導走而不是
        // 開新分頁：彈出視窗會被瀏覽器擋掉，而被擋掉時使用者只會看到「沒反應」。
        if (ok && redirectUrl) {
          window.location.assign(redirectUrl);
          return;
        }
        Modal[ok ? "success" : "error"]({
          title: action.label,
          width: detail ? 720 : 460,
          content: (
            <div>
              <p>{msg}</p>
              {detail && (
                <pre style={{ maxHeight: 360, overflow: "auto", fontSize: FONT.caption, background: "#fafafa", padding: 8 }}>
                  {JSON.stringify(detail, null, 2)}
                </pre>
              )}
            </div>
          ),
        });
      })
      .catch(() => message.error(`${action.label}失敗`))
      .finally(() => setRunning(null));
  };

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="載入系統設定失敗"
        description="請稍後重試；若持續發生請通知系統管理者。"
        action={<Button size="small" onClick={load}>重試</Button>}
      />
    );
  }

  if (!data) return <Spin />;

  const valueOf = (item) => (item.key in edited ? edited[item.key] : item.value);

  // 建議清單的解析放在這裡而非 SettingField —— 欄位元件只認得型別，
  // 不該知道「某個欄位的選項取決於另一個欄位」。頁面握有全部 items 與
  // 當前編輯值，由它解析後把結果交下去即可。
  const suggestionsFor = (item) => {
    if (!item.suggestions) return null;
    if (!item.suggestions_from) return item.suggestions;

    const source = data.items.find((i) => i.key === item.suggestions_from);

    return source ? item.suggestions[valueOf(source)] || [] : [];
  };

  // 條件顯示的解析同樣放在這裡而非 SettingField —— 欄位元件只認得型別，
  // 不該知道欄位之間的關係。被隱藏的欄位其值仍在資料庫裡，切回時還在。
  const isVisible = (item) => {
    if (!item.show_when) return true;

    const source = data.items.find((i) => i.key === item.show_when.field);

    return source ? valueOf(source) === item.show_when.equals : true;
  };

  return (
    // 限寬置中而不是只把內容置中：說明文字需要 640px 的可讀行寬，但卡片若維持
    // 滿版，1920px 下右側會空掉 1037px (佔卡片 61%)，而「儲存」被推到最右上角、
    // 離它要儲存的欄位一千多像素 —— 那正是使用者填完欄位卻忘記儲存的原因。
    <Card
      size="small"
      title="系統設定"
      style={{ maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}
      extra={<Button type="primary" loading={saving} onClick={save}>儲存</Button>}
    >
      {/* antd 4.19.5 的 Tabs 不支援 items prop (該 API 是 4.23 才加入)，
          未知的 prop 會被 rc-tabs 直接吐到最外層 div 的 DOM 屬性，導致完全不渲染任何分頁。
          這裡沿用本專案既有的 Tabs.TabPane children 寫法 (同 UserPage.jsx、TodoPage/index.jsx)。 */}
      <Tabs>
        {data.categories.map((category) => (
          <Tabs.TabPane tab={category.label} key={category.key}>
            {data.groups
              .filter((group) => group.category === category.key)
              .map((group) => (
                <div key={group.key} style={{ maxWidth: 640, marginBottom: 28 }}>
                  <Typography.Title level={5} style={{ marginBottom: group.description ? 4 : 12 }}>
                    {group.label}
                  </Typography.Title>
                  {group.description && (
                    <Typography.Paragraph type="secondary" style={{  }}>
                      {group.description}
                    </Typography.Paragraph>
                  )}
                  {/* 真正的 <ol>：換行會凸排在編號右側，一眼看得出哪幾行屬於同一步。
                      先前用 \n 加 pre-line，第二行貼齊左邊界，看起來像新的一項。
                      動作標題獨立成行，讀者可以只掃標題決定要不要細看說明。 */}
                  {!!(group.steps || []).length && (
                    <ol style={{ color: "rgba(0, 0, 0, 0.45)", paddingLeft: 20, marginBottom: 16 }}>
                      {group.steps.map((step) => (
                        <li key={step.title} style={{ marginBottom: 6 }}>
                          <Typography.Text strong style={{  }}>{step.title}</Typography.Text>
                          <div>{step.detail}</div>
                        </li>
                      ))}
                    </ol>
                  )}
                  {/* 只用來排版：欄位的值由 edited/valueOf 自行控制，沒有 name 的
                      Form.Item 不會註冊進 antd 的表單狀態。labelCol/wrapperCol 讓標籤成為
                      固定寬度的一欄，各欄位的輸入框才會對齊在同一條垂直線上。 */}
                  <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                    {data.items
                      .filter((item) => item.group === group.key)
                      .filter(isVisible)
                      .map((item) => (
                        <SettingField
                          key={item.key}
                          item={item}
                          value={valueOf(item)}
                          onChange={(v) => setEdited((prev) => ({ ...prev, [item.key]: v }))}
                          onClear={() => clear(item.key)}
                          suggestions={suggestionsFor(item)}
                          onRunAction={runAction}
                          actionRunning={running}
                          dynamicOptions={dynamicOptions[item.key]}
                          onLoadOptions={() => loadOptions(item)}
                          actionDisabledReason={disabledReason(group.key)}
                        />
                      ))}
                  </Form>
                  {/* 靠右：按鈕原本落在整個分組的最左邊，既沒對齊標籤欄也沒對齊輸入欄，
                      視覺上是懸空的。靠右對齊內容欄右緣，整頁的動作都收在右側。
                      Space 預設是 inline-flex，要改成 flex 並吃滿寬度，
                      justify-content 才有作用的對象。 */}
                  {!!(group.actions || []).length && (
                    <Space style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                      {group.actions.map((action) => (
                        <Tooltip key={action.endpoint} title={disabledReason(group.key)}>
                          <Button
                            loading={running === action.endpoint}
                            disabled={!!disabledReason(group.key)}
                            onClick={() => runAction(action)}
                          >
                            {action.label}
                          </Button>
                        </Tooltip>
                      ))}
                    </Space>
                  )}
                </div>
              ))}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </Card>
  );
}
